import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Plus, Trash2, Edit2, MessageSquareText, Save, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

interface QuickReply {
  id: string;
  title: string;
  content: string;
  category: string | null;
  is_active: boolean;
  created_at: string;
}

const AdminQuickReplies = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [replies, setReplies] = useState<QuickReply[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("عام");
  const [saving, setSaving] = useState(false);

  const fetchReplies = async () => {
    const { data, error } = await supabase
      .from("quick_replies")
      .select("*")
      .order("category", { ascending: true })
      .order("created_at", { ascending: false });

    if (!error && data) setReplies(data as QuickReply[]);
    setLoading(false);
  };

  useEffect(() => {
    fetchReplies();
  }, []);

  const resetForm = () => {
    setTitle("");
    setContent("");
    setCategory("عام");
    setEditingId(null);
  };

  const handleSave = async () => {
    if (!title.trim() || !content.trim()) return;
    setSaving(true);

    try {
      if (editingId) {
        const { error } = await supabase
          .from("quick_replies")
          .update({ title, content, category, updated_at: new Date().toISOString() })
          .eq("id", editingId);
        if (error) throw error;
        toast({ title: "تم التحديث", description: "تم تحديث الرد الجاهز بنجاح" });
      } else {
        const { error } = await supabase
          .from("quick_replies")
          .insert({ title, content, category, created_by: user?.id });
        if (error) throw error;
        toast({ title: "تمت الإضافة", description: "تم إضافة الرد الجاهز بنجاح" });
      }
      resetForm();
      setDialogOpen(false);
      fetchReplies();
    } catch (error) {
      console.error(error);
      toast({ title: "خطأ", description: "حدث خطأ أثناء الحفظ", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (reply: QuickReply) => {
    setEditingId(reply.id);
    setTitle(reply.title);
    setContent(reply.content);
    setCategory(reply.category || "عام");
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from("quick_replies").delete().eq("id", id);
    if (!error) {
      toast({ title: "تم الحذف", description: "تم حذف الرد الجاهز" });
      fetchReplies();
    }
  };

  const handleToggleActive = async (id: string, currentActive: boolean) => {
    await supabase.from("quick_replies").update({ is_active: !currentActive }).eq("id", id);
    fetchReplies();
  };

  const categories = [...new Set(replies.map((r) => r.category || "عام"))];

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card className="glass border-border">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-foreground flex items-center gap-2">
          <MessageSquareText className="w-5 h-5" />
          الردود الجاهزة ({replies.length})
        </CardTitle>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="w-4 h-4 ml-2" />
              إضافة رد جاهز
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]" dir="rtl">
            <DialogHeader>
              <DialogTitle>{editingId ? "تعديل الرد الجاهز" : "إضافة رد جاهز جديد"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-1 block">العنوان</label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="مثال: الترحيب بالعميل"
                  dir="rtl"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-1 block">التصنيف</label>
                <Input
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder="عام"
                  dir="rtl"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-1 block">محتوى الرد</label>
                <Textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="اكتب نص الرد الجاهز هنا..."
                  rows={4}
                  dir="rtl"
                />
              </div>
              <Button onClick={handleSave} disabled={saving || !title.trim() || !content.trim()} className="w-full">
                {saving ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : <Save className="w-4 h-4 ml-2" />}
                {editingId ? "تحديث" : "إضافة"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {replies.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            لا توجد ردود جاهزة بعد. أضف ردوداً لتسريع الرد على الاستفسارات المتكررة.
          </div>
        ) : (
          <div className="space-y-6">
            {categories.map((cat) => (
              <div key={cat}>
                <h3 className="text-sm font-semibold text-muted-foreground mb-3">{cat}</h3>
                <div className="space-y-3">
                  {replies
                    .filter((r) => (r.category || "عام") === cat)
                    .map((reply) => (
                      <div
                        key={reply.id}
                        className={`border rounded-lg p-4 transition-colors ${
                          reply.is_active ? "border-border bg-secondary/30" : "border-border/50 bg-secondary/10 opacity-60"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium text-foreground">{reply.title}</h4>
                              {!reply.is_active && (
                                <Badge variant="outline" className="text-xs">معطّل</Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground whitespace-pre-wrap line-clamp-3">
                              {reply.content}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleActive(reply.id, reply.is_active)}
                              title={reply.is_active ? "تعطيل" : "تفعيل"}
                            >
                              {reply.is_active ? "تعطيل" : "تفعيل"}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(reply)}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(reply.id)} className="text-destructive">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminQuickReplies;
